import React, { Suspense, lazy } from "react";
import "./App.css";
import "./styles/network-theme.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import usePageTracking from "./hooks/useAnalytics";

// Public Pages (loaded eagerly for SEO)
import Home from "./pages/Home";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import Projects from "./pages/Projects";
import Reviews from "./pages/Reviews";
import About from "./pages/About";
import News from "./pages/News";
import ProjectDetail from "./pages/ProjectDetail";
import Contact from "./pages/Contact";
import ServiceArea from "./pages/ServiceArea";
import Imprint from "./pages/Imprint";
import Privacy from "./pages/Privacy";
import LocationPage from "./pages/LocationPage";
import LocationServicePage from "./pages/LocationServicePage";
import ErdarbeitenPage from "./pages/ErdarbeitenPage";
import GartenpflegePage from "./pages/GartenpflegePage";

// Admin Pages (lazy loaded - only when needed)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminHome = lazy(() => import("./pages/admin/AdminHome"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const LeadDetail = lazy(() => import("./pages/admin/LeadDetail"));
const AdminPosts = lazy(() => import("./pages/admin/AdminPosts"));
const CreatePost = lazy(() => import("./pages/admin/CreatePost"));
const EditPost = lazy(() => import("./pages/admin/EditPost"));
const AdminImages = lazy(() => import("./pages/Admin/AdminImages"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminContactInfo = lazy(() => import("./pages/admin/AdminContactInfo"));
const AdminImpressum = lazy(() => import("./pages/admin/AdminImpressum"));
const AdminDatenschutz = lazy(() => import("./pages/admin/AdminDatenschutz"));
const AdminTowns = lazy(() => import("./pages/admin/AdminTowns"));
const AdminHero = lazy(() => import("./pages/admin/AdminHero"));
const AdminServiceCategories = lazy(() => import("./pages/admin/AdminServiceCategories"));
const AdminFAQs = lazy(() => import("./pages/admin/AdminFAQs"));
const AdminSEO = lazy(() => import("./pages/admin/AdminSEO"));
const AdminReviewSettings = lazy(() => import("./pages/admin/AdminReviewSettings"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminHomepageImages = lazy(() => import("./pages/admin/AdminHomepageImages"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminScheduledPosts = lazy(() => import("./pages/admin/AdminScheduledPosts"));

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<><Header /><Home /><Footer /></>} />
            <Route path="/leistungen" element={<><Header /><Services /><Footer /></>} />
            <Route path="/leistungen/erdarbeiten" element={<><Header /><ErdarbeitenPage /><Footer /></>} />
            <Route path="/leistungen/gartenpflege" element={<><Header /><GartenpflegePage /><Footer /></>} />
            <Route path="/leistungen/:serviceId" element={<><Header /><ServiceDetail /><Footer /></>} />
            <Route path="/projekte" element={<><Header /><Projects /><Footer /></>} />
            <Route path="/projekte/:slug" element={<><Header /><ProjectDetail /><Footer /></>} />
            <Route path="/bewertungen" element={<><Header /><Reviews /><Footer /></>} />
            <Route path="/ueber-uns" element={<><Header /><About /><Footer /></>} />
            <Route path="/neuigkeiten" element={<Navigate to="/projekte" replace />} />
            <Route path="/kontakt" element={<><Header /><Contact /><Footer /></>} />
            <Route path="/servicegebiet" element={<><Header /><ServiceArea /><Footer /></>} />
            <Route path="/impressum" element={<><Header /><Imprint /><Footer /></>} />
            <Route path="/datenschutz" element={<><Header /><Privacy /><Footer /></>} />
            
            {/* Location SEO Pages */}
            <Route path="/goettingen" element={<><Header /><LocationPage /><Footer /></>} />
            <Route path="/bovenden" element={<><Header /><LocationPage /><Footer /></>} />
            <Route path="/northeim" element={<><Header /><LocationPage /><Footer /></>} />
            <Route path="/einbeck" element={<><Header /><LocationPage /><Footer /></>} />
            <Route path="/uslar" element={<><Header /><LocationPage /><Footer /></>} />
            <Route path="/noerten-hardenberg" element={<><Header /><LocationPage /><Footer /></>} />
            <Route path="/hardegsen" element={<><Header /><LocationPage /><Footer /></>} />
            <Route path="/moringen" element={<><Header /><LocationPage /><Footer /></>} />
            
            {/* Dynamic Location + Service SEO Pages (hidden from nav, indexed by Google) */}
            <Route path="/gartenpflege/:location" element={<><Header /><LocationServicePage service="gartenpflege" /><Footer /></>} />
            <Route path="/erdarbeiten/:location" element={<><Header /><LocationServicePage service="erdarbeiten" /><Footer /></>} />
            {/* Alternative Routen für Erdarbeiten (häufige Suchbegriffe) */}
            <Route path="/baggerarbeiten/:location" element={<><Header /><LocationServicePage service="erdarbeiten" /><Footer /></>} />
            <Route path="/minibagger/:location" element={<><Header /><LocationServicePage service="erdarbeiten" /><Footer /></>} />
            <Route path="/aushub/:location" element={<><Header /><LocationServicePage service="erdarbeiten" /><Footer /></>} />
            <Route path="/tiefbau/:location" element={<><Header /><LocationServicePage service="erdarbeiten" /><Footer /></>} />
            <Route path="/baumfaellung/:location" element={<><Header /><LocationServicePage service="baumfaellung" /><Footer /></>} />
            {/* Alternative Routen für Baumfällung */}
            <Route path="/baumpflege/:location" element={<><Header /><LocationServicePage service="baumfaellung" /><Footer /></>} />
            <Route path="/baumschnitt/:location" element={<><Header /><LocationServicePage service="baumfaellung" /><Footer /></>} />
            <Route path="/stubbenfraeser/:location" element={<><Header /><LocationServicePage service="baumfaellung" /><Footer /></>} />
            <Route path="/hochdruckreinigung/:location" element={<><Header /><LocationServicePage service="hochdruckreinigung" /><Footer /></>} />
            {/* Alternative Routen für Hochdruckreinigung */}
            <Route path="/terrassenreinigung/:location" element={<><Header /><LocationServicePage service="hochdruckreinigung" /><Footer /></>} />
            <Route path="/pflasterreinigung/:location" element={<><Header /><LocationServicePage service="hochdruckreinigung" /><Footer /></>} />
            <Route path="/entsorgung/:location" element={<><Header /><LocationServicePage service="entsorgung" /><Footer /></>} />
            {/* Alternative Routen für Entsorgung */}
            <Route path="/gruenschnitt/:location" element={<><Header /><LocationServicePage service="entsorgung" /><Footer /></>} />
            {/* Alternative Routen für Gartenpflege */}
            <Route path="/gartenservice/:location" element={<><Header /><LocationServicePage service="gartenpflege" /><Footer /></>} />
            <Route path="/rasenmaehen/:location" element={<><Header /><LocationServicePage service="gartenpflege" /><Footer /></>} />
            <Route path="/heckenschnitt/:location" element={<><Header /><LocationServicePage service="gartenpflege" /><Footer /></>} />
            <Route path="/gaertner/:location" element={<><Header /><LocationServicePage service="gartenpflege" /><Footer /></>} />

            {/* Admin Routes (lazy loaded) */}
            <Route path="/admin/login" element={<Suspense fallback={<div style={{padding:'100px',textAlign:'center'}}>Laden...</div>}><AdminLogin /></Suspense>} />
            <Route path="/admin" element={<ProtectedRoute><Suspense fallback={null}><AdminHome /></Suspense></ProtectedRoute>} />
            <Route path="/admin/leads" element={<ProtectedRoute><Suspense fallback={null}><AdminLeads /></Suspense></ProtectedRoute>} />
            <Route path="/admin/leads/:leadId" element={<ProtectedRoute><Suspense fallback={null}><LeadDetail /></Suspense></ProtectedRoute>} />
            <Route path="/admin/posts" element={<ProtectedRoute><Suspense fallback={null}><AdminPosts /></Suspense></ProtectedRoute>} />
            <Route path="/admin/posts/new" element={<ProtectedRoute><Suspense fallback={null}><CreatePost /></Suspense></ProtectedRoute>} />
            <Route path="/admin/posts/:postId/edit" element={<ProtectedRoute><Suspense fallback={null}><EditPost /></Suspense></ProtectedRoute>} />
            <Route path="/admin/images" element={<ProtectedRoute><Suspense fallback={null}><AdminImages /></Suspense></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><Suspense fallback={null}><AdminSettings /></Suspense></ProtectedRoute>} />
            <Route path="/admin/contact-info" element={<ProtectedRoute><Suspense fallback={null}><AdminContactInfo /></Suspense></ProtectedRoute>} />
            <Route path="/admin/impressum" element={<ProtectedRoute><Suspense fallback={null}><AdminImpressum /></Suspense></ProtectedRoute>} />
            <Route path="/admin/datenschutz" element={<ProtectedRoute><Suspense fallback={null}><AdminDatenschutz /></Suspense></ProtectedRoute>} />
            <Route path="/admin/towns" element={<ProtectedRoute><Suspense fallback={null}><AdminTowns /></Suspense></ProtectedRoute>} />
            <Route path="/admin/hero" element={<ProtectedRoute><Suspense fallback={null}><AdminHero /></Suspense></ProtectedRoute>} />
            <Route path="/admin/services" element={<ProtectedRoute><Suspense fallback={null}><AdminServiceCategories /></Suspense></ProtectedRoute>} />
            <Route path="/admin/faqs" element={<ProtectedRoute><Suspense fallback={null}><AdminFAQs /></Suspense></ProtectedRoute>} />
            <Route path="/admin/seo" element={<ProtectedRoute><Suspense fallback={null}><AdminSEO /></Suspense></ProtectedRoute>} />
            <Route path="/admin/reviews" element={<ProtectedRoute><Suspense fallback={null}><AdminReviewSettings /></Suspense></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute><Suspense fallback={null}><AdminUsers /></Suspense></ProtectedRoute>} />
            <Route path="/admin/homepage-images" element={<ProtectedRoute><Suspense fallback={null}><AdminHomepageImages /></Suspense></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute><Suspense fallback={null}><AdminAnalytics /></Suspense></ProtectedRoute>} />
            <Route path="/admin/posts/scheduled" element={<ProtectedRoute><Suspense fallback={null}><AdminScheduledPosts /></Suspense></ProtectedRoute>} />
          </Routes>
          <PageTracker />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

// Component to track page views
function PageTracker() {
  usePageTracking();
  return null;
}

export default App;
